import React, { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { LOGO_URL } from "@/lib/brand";
import InstallPrompt from "@/components/InstallPrompt";

import AppShell from "@/components/layout/AppShell";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import PublicTrace from "@/pages/PublicTrace";
import Landing from "@/pages/Landing";
import { CONSOLE_PATH } from "@/lib/consoleConfig";

const Console = lazy(() => import("@/pages/Console"));
const BuyerDesk = lazy(() => import("@/pages/BuyerDesk"));

const FasalDoctor = lazy(() => import("@/pages/FasalDoctor"));
const HerbTrace = lazy(() => import("@/pages/HerbTrace"));
const Land = lazy(() => import("@/pages/Land"));
const Crops = lazy(() => import("@/pages/Crops"));
const Operations = lazy(() => import("@/pages/Operations"));
const Planning = lazy(() => import("@/pages/Planning"));
const CheckIn = lazy(() => import("@/pages/CheckIn"));
const CropCheck = lazy(() => import("@/pages/CropCheck"));
const PlantWizard = lazy(() => import("@/pages/PlantWizard"));
const PestMap = lazy(() => import("@/pages/PestMap"));
const CropProblems = lazy(() => import("@/pages/CropProblems"));
const SprayCheck = lazy(() => import("@/pages/SprayCheck"));
const ReportEvent = lazy(() => import("@/pages/ReportEvent"));
const Explore = lazy(() => import("@/pages/Explore"));
const Saathi = lazy(() => import("@/pages/Saathi"));
const Resources = lazy(() => import("@/pages/Resources"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Equipment = lazy(() => import("@/pages/Equipment"));
const Weather = lazy(() => import("@/pages/Weather"));
const Market = lazy(() => import("@/pages/Market"));
const Satellite = lazy(() => import("@/pages/Satellite"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const AI = lazy(() => import("@/pages/AI"));
const Documents = lazy(() => import("@/pages/Documents"));
const CRM = lazy(() => import("@/pages/CRM"));
const Schemes = lazy(() => import("@/pages/Schemes"));
const Team = lazy(() => import("@/pages/Team"));
const Export = lazy(() => import("@/pages/Export"));
const Profile = lazy(() => import("@/pages/Profile"));
const MeraSeason = lazy(() => import("@/pages/MeraSeason"));

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-2xl animate-spin">🌱</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <p className="text-6xl font-bold text-primary">404</p>
      <h2 className="text-xl font-semibold mt-3">Page not found</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm">This page doesn't exist. Let's get you back to your farm.</p>
      <a href="/" className="mt-5 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium" data-testid="back-home">Back to Dashboard</a>
    </div>
  );
}

function Splash() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center aurora">
      <img src={LOGO_URL} alt="Agro Madat" className="h-20 w-20 rounded-2xl object-contain bg-white shadow-md animate-pulse mb-3" />
      <p className="font-bold text-lg">Agro Madat</p>
      <p className="text-xs text-muted-foreground">किसान का सच्चा साथी</p>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Auth /></PublicOnly>} />
      <Route path={CONSOLE_PATH} element={<Suspense fallback={<PageFallback />}><Console /></Suspense>} />
      <Route path="/desk/*" element={<Protected><Suspense fallback={<PageFallback />}><BuyerDesk /></Suspense></Protected>} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/explore" element={<Suspense fallback={<PageFallback />}><Explore /></Suspense>} />
      <Route path="/trace/:id" element={<PublicTrace />} />
      <Route path="/season" element={<Protected><Suspense fallback={<PageFallback />}><MeraSeason /></Suspense></Protected>} />
      {!loading && !user && <Route path="/" element={<Landing />} />}
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/fasal-doctor" element={<Suspense fallback={<PageFallback />}><FasalDoctor /></Suspense>} />
        <Route path="/herb-trace" element={<Suspense fallback={<PageFallback />}><HerbTrace /></Suspense>} />
        <Route path="/farms" element={<Suspense fallback={<PageFallback />}><Land /></Suspense>} />
        <Route path="/land" element={<Navigate to="/farms" replace />} />
        <Route path="/fields" element={<Navigate to="/farms" replace />} />
        <Route path="/crops" element={<Suspense fallback={<PageFallback />}><Crops /></Suspense>} />
        <Route path="/operations" element={<Suspense fallback={<PageFallback />}><Operations /></Suspense>} />
        <Route path="/check-in" element={<Suspense fallback={<PageFallback />}><CheckIn /></Suspense>} />
        <Route path="/crop-check" element={<Suspense fallback={<PageFallback />}><CropCheck /></Suspense>} />
        <Route path="/plant" element={<Suspense fallback={<PageFallback />}><PlantWizard /></Suspense>} />
        <Route path="/pest-map" element={<Suspense fallback={<PageFallback />}><PestMap /></Suspense>} />
        <Route path="/planning" element={<Suspense fallback={<PageFallback />}><Planning /></Suspense>} />
        <Route path="/crop-problems" element={<Suspense fallback={<PageFallback />}><CropProblems /></Suspense>} />
        <Route path="/spray-check" element={<Suspense fallback={<PageFallback />}><SprayCheck /></Suspense>} />
        <Route path="/report" element={<Suspense fallback={<PageFallback />}><ReportEvent /></Suspense>} />
        <Route path="/saathi" element={<Suspense fallback={<PageFallback />}><Saathi /></Suspense>} />
        <Route path="/resources" element={<Suspense fallback={<PageFallback />}><Resources /></Suspense>} />
        <Route path="/inventory" element={<Suspense fallback={<PageFallback />}><Inventory /></Suspense>} />
        <Route path="/accounting" element={<Suspense fallback={<PageFallback />}><Accounting /></Suspense>} />
        <Route path="/equipment" element={<Suspense fallback={<PageFallback />}><Equipment /></Suspense>} />
        <Route path="/weather" element={<Suspense fallback={<PageFallback />}><Weather /></Suspense>} />
        <Route path="/market" element={<Suspense fallback={<PageFallback />}><Market /></Suspense>} />
        <Route path="/satellite" element={<Suspense fallback={<PageFallback />}><Satellite /></Suspense>} />
        <Route path="/analytics" element={<Suspense fallback={<PageFallback />}><Analytics /></Suspense>} />
        <Route path="/ai" element={<Suspense fallback={<PageFallback />}><AI /></Suspense>} />
        <Route path="/documents" element={<Suspense fallback={<PageFallback />}><Documents /></Suspense>} />
        <Route path="/crm" element={<Suspense fallback={<PageFallback />}><CRM /></Suspense>} />
        <Route path="/schemes" element={<Suspense fallback={<PageFallback />}><Schemes /></Suspense>} />
        <Route path="/team" element={<Suspense fallback={<PageFallback />}><Team /></Suspense>} />
        <Route path="/export" element={<Suspense fallback={<PageFallback />}><Export /></Suspense>} />
        <Route path="/profile" element={<Suspense fallback={<PageFallback />}><Profile /></Suspense>} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <InstallPrompt />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </AppProvider>
  );
}
